/**
 * Draft model for the OVS split editors — pure, so the save rules are
 * unit-testable without React.
 *
 * ## Why this exists (Sheila, 2026-08-26)
 *
 * *"OVS is not allowing them to save/update number… These fields are all
 * uneditable… They should all be editable… And need save button."*
 *
 * The previous editor rendered an INHERITED cell as an empty input with the
 * inherited number as a PLACEHOLDER, and saved on a 600 ms debounce. Both
 * halves of that read as broken:
 *
 *   - The field LOOKS filled but is empty. Click into "70", press Backspace:
 *     nothing happens. Press an arrow key: nothing happens. Type "5" and the
 *     cell becomes 5%, not 75%. Every campus in the screenshots was in this
 *     state (every chip read "This campus"/"Default (100%)" with `owned:
 *     false`), so the whole dialog was effectively read-only.
 *   - Nothing said an edit had been saved, and closing the dialog inside the
 *     debounce window dropped the edit on the floor — the timer died with the
 *     unmounting input and `onBlur` never ran.
 *
 * So: every cell now renders its EFFECTIVE number as a real, editable value,
 * and nothing is written until the user presses Save.
 *
 * ## The invariant that survives
 *
 * `split-provenance.ts` states it: **an inherited value that is merely being
 * DISPLAYED must never be persisted.** Round-tripping the district's 70% into
 * a stored campus row detaches that campus permanently, and a later district
 * edit silently stops reaching it — which is the bug that shipped 1,526
 * unwanted milk cartons.
 *
 * The placeholder was one way to enforce it. It is not the only way, and it
 * was the hostile one. `draftActionFor` enforces the same rule from the other
 * end: **a cell is written only when the typed number DIFFERS from what the
 * cell already resolves to.** Displaying 70 and leaving it alone writes
 * nothing; typing 7, then 0, back to exactly 70 also writes nothing. Only a
 * real change becomes a row.
 *
 * The one capability this trades away: a campus cannot pin its own row at
 * exactly the district's current number (to freeze itself against future
 * district edits) by typing that number. That is deliberate — the accidental
 * direction of that write is the expensive one, and nobody has asked for the
 * deliberate one.
 */

import type { SplitCell } from './split-provenance'

/** Every rate/share is stored in basis points; 10000 = 100%. */
export const FULL_RATE_BPS = 10000

/**
 * Convert a percentage string (e.g. "50" or "50.5") to basis points.
 * Empty string returns null (caller can decide whether to ignore the
 * edit or remove the row entirely). Invalid → null too.
 */
export function percentStringToBps(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    return null
  }
  const bps = Math.round(parsed * 100)
  if (bps < 0 || bps > FULL_RATE_BPS) {
    return null
  }
  return bps
}

/** Render basis points back to a human-friendly percentage string. */
export function bpsToPercentString(bps: number): string {
  // Trim trailing zeros so 5000 → "50" not "50.00".
  const pct = bps / 100
  return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * The number a cell resolves to TODAY: this tier's own value, else the value
 * it inherits, else the platform 100%. This is what the input shows, and it is
 * the baseline every save decision compares against.
 */
export function effectiveCellBps(cell: SplitCell): number {
  return cell.value ?? cell.inheritedBps ?? FULL_RATE_BPS
}

/** The input's value for a cell the user has not typed into. */
export function cellDisplayValue(cell: SplitCell): string {
  return bpsToPercentString(effectiveCellBps(cell))
}

/** The value a cell falls back to once its own row is cleared. */
export function clearedCellValue(cell: SplitCell): string {
  return bpsToPercentString(cell.inheritedBps ?? FULL_RATE_BPS)
}

/** The write one typed cell implies. */
export type SplitDraftAction = { kind: 'upsert'; bps: number } | { kind: 'clear' }

/**
 * The write a raw typed string implies for one cell — `null` when it implies
 * none (unparseable, unchanged, or an empty box on a cell that already
 * inherits).
 *
 * Emptying the box is how a user drops an override back to inherited; it is
 * never a save of 0%.
 */
export function draftActionFor(cell: SplitCell, raw: string): SplitDraftAction | null {
  if (raw.trim() === '') {
    return cell.owned ? { kind: 'clear' } : null
  }
  const bps = percentStringToBps(raw)
  if (bps === null) {
    return null
  }
  // Unchanged is unchanged at BOTH tiers: an owned cell re-typed to its own
  // stored number writes nothing, and an inherited cell re-typed to the number
  // it already inherits must not become a stored override (see the header).
  return bps === effectiveCellBps(cell) ? null : { kind: 'upsert', bps }
}

/** True when `raw` is neither empty nor a parseable 0–100 percentage. */
export function isDraftValueInvalid(raw: string): boolean {
  return raw.trim() !== '' && percentStringToBps(raw) === null
}
